export const POLICIES = {
  'help-center': {
    title: 'Help Center',
    summary: 'Fast support, real humans, and all the how-tos for RivalCam.',
    accent: '#0b7fab',
    content: `# Help Center

Welcome to the RivalCam Help Center. Here you'll find answers to the questions every fan, voter, and rivalry creator asks most often.

## Trending Topics
- Getting your account verified
- How to join or create a rivalry
- Understanding the vote percentage system
- Resetting your password or two-factor authentication
- Reporting abusive behavior or cheating

## Quick Answers

### How do I join a rivalry?
Open RivalCam, browse the live or upcoming rivalries on the Home screen, tap a matchup, and hit **Vote** for your team. Your vote immediately moves the percentage in your team's favor.

### How do credits work?
Credits are earned by voting consistently, sharing rivalries, and winning battles. You can use credits to boost visibility for your team or unlock special features inside the app.

### What if I can't log in?
1. Tap **Forgot Password** on the sign-in screen.
2. Enter your registered email address.
3. Check your inbox for a reset link (also check spam).
4. Still stuck? Email us with your username and the device you're using.

### Can I report a rivalry or a user?
Yes. Tap the three-dot menu on any rivalry or profile, select **Report**, pick a reason, and add context. Reports go directly into our Trust & Safety queue and are reviewed within hours.

### How do I delete my account?
Go to **Settings → Account → Delete Account**. You'll receive a confirmation email summarizing any data we retain for legal compliance.

## Contact Channels
- Email: [support@meanc.net](mailto:support@meanc.net)
- App Feedback: Use the **Feedback** button inside Settings
- Business: [services@meanc.net](mailto:services@meanc.net)

We log every contact and aim to reply within one business day.`,
  },

  career: {
    title: 'Careers',
    summary: 'Join the builders behind the world\'s most energetic fan rivalry platform.',
    accent: '#d46c00',
    content: `# Careers at RivalCam

We are building the most engaging live fan rivalry platform on the planet. That means hiring people who care deeply about sport, community, and real-time technology.

## Why People Join
- **Impact:** Ship features used by fans across every continent.
- **Growth:** Individual learning budgets, mentorship, and cross-functional rotational programs.
- **Flexibility:** Hybrid and fully-remote roles across North America, Europe, and Africa.

## Teams Hiring Now
1. **Product & Design** – Craft the voting experience, rivalry feeds, and fan-zone tools.
2. **Engineering** – Real-time vote systems, live leaderboards, and mobile performance.
3. **Community & Safety** – Keep every rivalry respectful, fair, and on-topic.
4. **Revenue & Partnerships** – Work with leagues, clubs, sports media, and brands.

## Application Process
1. Apply with your resume, portfolio, or a quick note about your passion for sport and tech.
2. Meet the hiring manager for a culture and craft conversation.
3. Complete a short paid take-home challenge tailored to the role.
4. Final round with cross-functional teammates.

Questions? Email [talent@meanc.net](mailto:talent@meanc.net). No open role that fits? Send us a note anyway — great teammates are welcome year-round.`,
  },

  forum: {
    title: 'Forum Standards',
    summary: 'Keep rivalry discussions clean, insightful, and fair.',
    accent: '#7a3eb1',
    content: `# Forum Standards

The RivalCam Forum is where fans, analysts, and die-hard supporters debate rivalries, trades, and matchups. To keep the conversation healthy, we run the forum with the same standards enforced inside the app.

## Posting Guidelines
- Share firsthand insights, stats, or real match experiences. Wild speculation belongs in the Lounge channel.
- Credit your sources when posting clips, stats, or quotes from other publications.
- Use the **Spoiler** tag for unreleased match results or transfer news.

## Moderation Flow
1. Community members can flag toxic, off-topic, or misleading replies.
2. Moderators review every flag within four hours.
3. Violations earn strikes; repeated abuse results in platform-wide account suspension.

## Best Practices
- Pin actionable feedback when answering fan questions about rivalries.
- Keep recruitment or sponsorship requests in the dedicated Partnership board.
- Move heated personal disputes to private DMs rather than public threads.

Need a new sub-channel? Pitch it to [community@meanc.net](mailto:community@meanc.net) — we review every Friday.`,
  },

  terms: {
    title: 'Terms & Conditions',
    summary: 'The rules of the platform and how we operate responsibly.',
    accent: '#195167',
    content: `# Terms & Conditions

These Terms govern your use of RivalCam's app, website, and all related services operated by MEANC Technologies.

**Effective date:** January 1, 2025
**Last updated:** May 2026

## Eligibility
- You must be at least **13 years old** to use RivalCam (or older where local law requires a higher age limit).
- By creating an account, you confirm you meet the age requirement and that all registration information you provide is accurate.

## Core Commitments
- Keep your login credentials private and notify us immediately if you suspect unauthorized access.
- Only share content you own or have explicit permission to post. Respect all intellectual-property and sports-broadcast rights.
- Never attempt to reverse-engineer, scrape, manipulate vote counts, or overload our infrastructure.
- Do not use automated bots, scripts, or fake accounts to influence rivalry outcomes.

## Service Usage
1. **Rivalries & Voting** – We host live rivalries and aggregate fan votes. You grant RivalCam a license to display your votes, comments, and fan-zone posts within the platform.
2. **Messaging** – Private chats are encrypted in transit. We may scan messages for spam and abuse indicators consistent with our Privacy Policy.
3. **Payments & Credits** – Any in-app credit purchases or subscription fees are processed by PCI-compliant payment partners.

## Account Actions
- You can delete your account at any time from **Settings → Account → Delete Account**. You will receive a confirmation email summarizing the data we retain for legal purposes.
- We may suspend or terminate accounts that violate these Terms, our Community Guidelines, or applicable law — with or without prior notice depending on severity.

## Limitation of Liability
RivalCam is provided "as is." We are not liable for indirect, incidental, or consequential damages arising from your use of the service.

## Governing Law
These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-law provisions.

Using RivalCam means you accept these Terms. Questions? Reach out to [legal@meanc.net](mailto:legal@meanc.net).`,
  },

  privacy: {
    title: 'Privacy Policy',
    summary: 'How we collect, safeguard, and give you control over your data.',
    accent: '#008c72',
    content: `# Privacy Policy

**Effective date:** January 1, 2025
**Last updated:** May 2026

RivalCam is operated by MEANC Technologies. We designed RivalCam to let fans live their rivalry passionately — without compromising their privacy. When you use our services, you trust us with your data, and we take that responsibility seriously.

## Data We Collect
- **Account information:** name, username, email address, date of birth, and language preferences.
- **Content you submit:** votes, comments, rivalry posts, and any media you upload.
- **Usage & device data:** IP address, device identifiers, operating system, app version, and interaction logs used for analytics, fraud detection, and performance optimization.
- **Camera & microphone (mobile only):** accessed only when you explicitly record or upload content inside the app. We do not store raw camera/microphone streams.

## How We Use Your Data
1. Deliver personalized rivalry feeds, vote tallies, and notifications.
2. Keep the platform safe through automated and human review systems.
3. Improve products by running A/B experiments and measuring engagement.
4. Comply with legal requests and protect the rights of users, creators, and partners.
5. Process payments and manage credit balances.

## Data Sharing
We do not sell your personal data. We share data only with:
- **Service providers** (cloud infrastructure, analytics, payment processors) under strict data-processing agreements.
- **Law enforcement** when required by valid legal process.
- **Sports partners** only in aggregate, anonymized form (e.g., total votes per rivalry).

## Your Controls
- Update or export your data inside **Settings → Privacy**.
- Disable marketing emails inside **Settings → Notifications**.
- Request full account and data deletion at any time by emailing [privacy@meanc.net](mailto:privacy@meanc.net) or using the in-app delete flow.
- Opt out of non-essential analytics via **Settings → Privacy → Analytics**.

## Data Retention
We retain account data for as long as your account is active. After deletion, we purge personal data within **30 days**, except where retention is required by law.

## Children's Privacy
RivalCam is not directed at children under 13. If we become aware that a child under 13 has created an account, we will delete it promptly. Parents may contact [privacy@meanc.net](mailto:privacy@meanc.net).

## Changes to This Policy
We publish material updates to this policy via in-app notification and email at least 14 days before they take effect.

Contact our privacy team at [privacy@meanc.net](mailto:privacy@meanc.net).`,
  },

  'community-guidelines': {
    title: 'Community Guidelines',
    summary: 'The culture code for everyone on RivalCam.',
    accent: '#c0392b',
    content: `# Community Guidelines

RivalCam connects fans who love sport and competition. To protect that energy — and keep rivalries friendly — we enforce clear rules for everyone on the platform.

## Respect People
- Hate speech, harassment, or discrimination based on race, religion, gender, nationality, or sexual orientation is removed immediately.
- Doxxing, credible threats, or sharing private content without consent results in a permanent ban.
- No bullying fans for their team allegiance. Rivalry is fun — cruelty is not.

## Respect the Game
- Match-fixing tips, illegal gambling promotions, and counterfeit sports merchandise are strictly prohibited.
- Share only clips, highlights, and statistics that you have the rights to post.
- Do not manipulate vote counts using bots, multiple accounts, or coordinated inauthentic behavior.

## Respect the Platform
1. Verify information before sharing breaking transfer news or match results.
2. Disclose partnerships, paid promotions, or brand sponsorships clearly.
3. Use the official in-app **Report** tool rather than calling out other users publicly.

## Consequences
- **First violation:** Content warning and removal.
- **Repeated violations:** Strikes, temporary suspension, or permanent removal depending on severity.
- **Severe violations** (e.g., threats, CSAM, doxxing): immediate permanent ban and potential referral to law enforcement.

Questions about a moderation decision? Email [community@meanc.net](mailto:community@meanc.net).`,
  },

  cookies: {
    title: 'Cookies Policy',
    summary: 'Understand, manage, and fine-tune your cookie preferences.',
    accent: '#9c6b1c',
    content: `# Cookies Policy

**Effective date:** January 1, 2025
**Last updated:** May 2026

Cookies and similar technologies help us provide a fast, personalized RivalCam experience. Here is exactly how we use them.

## Types of Cookies

| Type | Purpose |
|------|---------|
| **Essential** | Authentication sessions, security tokens, load balancing. Cannot be disabled. |
| **Analytics** | Understand which features fans use most, measure rivalry engagement, improve video delivery. |
| **Personalization** | Remember your preferred dark/light mode, language, and notification settings. |
| **Marketing** | Measure the effectiveness of promotional campaigns on partner platforms. |

## What We Do NOT Do
- We never sell cookie data to third parties.
- We do not allow external advertising networks to place tracking cookies on RivalCam without contractual safeguards and your consent.

## Managing Your Preferences
- **In-app:** Go to **Settings → Privacy → Cookie Controls** at any time.
- **Browser-level:** Most browsers let you block or delete cookies in their privacy settings. Note that blocking essential cookies will prevent you from staying logged in.
- **First visit:** A consent banner appears on your first visit to rivalcam.app — you choose which categories to accept.

## Third-Party Services
We use the following categories of third-party tools that may set cookies: analytics providers (e.g., aggregated usage dashboards), payment processors (session tokens only), and CDN providers (performance caching).

For questions email [privacy@meanc.net](mailto:privacy@meanc.net).`,
  },

  copyright: {
    title: 'Copyright Policy',
    summary: 'Protection for creators, fans, clubs, and partners across the network.',
    accent: '#2c3e50',
    content: `# Copyright Policy

**Effective date:** January 1, 2025

RivalCam respects the intellectual property rights of content creators, sports leagues, clubs, and broadcasters — and we expect every community member to do the same.

## What Is Protected
- Match footage, highlight clips, and broadcast streams owned by sports leagues or broadcasters.
- Team logos, crests, and branding assets.
- Original fan content (videos, commentary, graphics) uploaded by other RivalCam users.

## Reporting Infringement
If you believe content on RivalCam infringes your copyright:

1. Collect the URL or screenshot of the infringing content along with any supporting evidence of your ownership.
2. Email [copyright@meanc.net](mailto:copyright@meanc.net) with the subject line **DMCA Notice**.
3. Include:
   - Your full name and contact information.
   - A description of the copyrighted work and the infringing content.
   - A statement confirming you own the rights or are authorized to act on behalf of the rights owner.
   - Your electronic or physical signature.

We will respond to valid DMCA notices within **3 business days** and remove or disable access to infringing content promptly.

## Repeat Offenders
Accounts that repeatedly violate copyright will be suspended or permanently removed. We also notify partner leagues and clubs when infringement is tied to verified users.

## Counter Notices
If you believe we removed your content in error:
1. Reply to the DMCA email with a detailed explanation and proof of license or ownership.
2. Include a statement that you consent to the jurisdiction of your local federal court.
3. We will review counter notices within **10 business days**.

Questions? Email [copyright@meanc.net](mailto:copyright@meanc.net).`,
  },
};

export const POLICY_NAV = [
  { slug: 'help-center',          label: 'Help Center' },
  { slug: 'career',               label: 'Career' },
  { slug: 'forum',                label: 'Forum' },
  { slug: 'terms',                label: 'Terms & Conditions' },
  { slug: 'privacy',              label: 'Privacy Policy' },
  { slug: 'community-guidelines', label: 'Community Guidelines' },
  { slug: 'cookies',              label: 'Cookies Policy' },
  { slug: 'copyright',            label: 'Copyright Policy' },
];
